<template>
    <v-row justify="center">
        <!-- Delete Task Dialog -->
        <DeleteDialog
            :dialog.sync="deleteTaskDialog"
            :item="growthTask"
            v-on:confirmed="deleteTaskConfirmed"
        >
            <template v-slot:title>Delete Task</template>
            <template v-slot:content
                >Are you sure you want to delete this task?</template
            >
        </DeleteDialog>

        <InventoryChangeReasonTagEditDialog
            :dialog.sync="inventoryChangeReasonTagEditDialog"
            :changeReason="editingChangeReasonType"
            v-on:item-created="onInventoryChangeReasonTagAdded"
        >
        </InventoryChangeReasonTagEditDialog>

        <!-- Lock growth task dialog -->
        <Lock
            :lockDialog="lockDialog"
            :growthTask="growthTask"
            :taskColor="taskColor"
            :customers="customers"
            :rules="rules"
            :lockingTaskData="lockingTaskData"
            :isSaving="isSaving"
            v-on:closeLockDialog="closeLockDialog"
            v-on:validatingForm="onLockGrowthTaskConfirmed"
            v-on:unlockGrowthTask="onUnlockGrowthTaskConfirmed"
            :isLoading="$apollo.queries.customers.loading"
        ></Lock>

        <!-- Update task plan dialog -->
        <UpdateTask
            ref="UpdateTaskDialogRef"
            :updateTaskPlanDialog="updateTaskPlanDialog"
            v-on:plsCloseUpdateTaskPlanDialog="closeUpdateTaskPlanDialog"
        ></UpdateTask>

        <!-- Update supplier dialog -->
        <UpdateSupplierDialog
            :supplierDialog="supplierDialog"
            :growthTask="growthTask"
            :taskColor="taskColor"
            :suppliers="suppliers"
            :isLoading="$apollo.queries.suppliers.loading"
            v-on:closeDialog="closeSupplierDialog"
            :selectedSupplierId="selectedSupplierId"
            :isSaving="isSaving"
            v-on:confirmSupplierUpdateForm="onUpdateSupplierConfirmed"
            v-on:newSelectedSupplierId="setNewSupplierId"
        ></UpdateSupplierDialog>

        <!-- Split growth task dialog -->
        <SplitGrowthTaskDialog
            :splitDialog="splitDialog"
            :growthTask="growthTask"
            :isSaving="isSaving"
            :taskColor="taskColor"
            :rules="rules"
            :splitingTaskData="splitingTaskData"
            :splitReasons="splitReasons"
            :queryLoading="
                $apollo.queries.tenantInventoryChangeReasonTags.loading
            "
            v-on:closeWindow="closeSplitDialog"
            v-on:validatingSplitForm="onSplitGrowthTaskConfirmed"
            v-on:addSplitReasonTag="addInventorySplitReasonTag"
        ></SplitGrowthTaskDialog>

        <!-- splited tasks with QR code -->
        <QRCodeSplitedTasks
            :splitedTasksDialog="splitedTasksDialog"
            :splittedGrowthTask="splittedGrowthTask"
            v-on:closeSplittedDialog="closeSplittedTaskDialog"
        ></QRCodeSplitedTasks>

        <!-- Update growth task inventory dialog -->
        <GrowthTaskInventoryUpdateDialog
            :changeInventoryDialog="changeInventoryDialog"
            :growthTask="growthTask"
            :isSaving="isSaving"
            :taskColor="taskColor"
            :changeInventoryData="changeInventoryData"
            :rules="rules"
            :manualReasons="manualReasons"
            :loadingChangeReasons="
                $apollo.queries.tenantInventoryChangeReasonTags.loading
            "
            v-on:closeInventoryDialog="closeChangeInventoryDialog"
            v-on:InventoryChangeFormValidation="onChangeInventoryConfirmed"
            :addInventoryManualReasonTag="addInventoryManualReasonTag"
        ></GrowthTaskInventoryUpdateDialog>

        <!-- select task plant tag dialog -->
        <v-dialog
            v-if="selectTagDialog"
            v-model="selectTagDialog"
            persistent
            width="600px"
        >
            <v-form ref="selectTagForm">
                <v-card>
                    <v-card-title class="primary white--text"
                        >Select Tag</v-card-title
                    >

                    <v-card-text class="pb-0">
                        <v-toolbar class="py-2" outlined flat>
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                    <v-toolbar-title
                                        class="title pr-2"
                                        v-on="on"
                                    >
                                        {{
                                            growthTask
                                                ? growthTask.plant.name
                                                : ""
                                        }}
                                        <div
                                            class="
                                                mt-n1
                                                grey--text
                                                text--darken-1
                                                caption
                                            "
                                            v-if="growthTask"
                                        >
                                            {{
                                                (growthTask
                                                    ? growthTask.growthStartDate
                                                    : "1970-01-01")
                                                    | moment("YYYY-[W]WW")
                                            }}
                                        </div>
                                    </v-toolbar-title>
                                </template>
                                <span>{{
                                    growthTask ? growthTask.plant.name : ""
                                }}</span>
                            </v-tooltip>

                            <v-spacer />
                            <v-icon :color="taskColor" class="mr-1">eco</v-icon>
                            <span :class="taskColor + `--text headline`">{{
                                growthTask ? growthTask.totalInventory : 0
                            }}</span>
                        </v-toolbar>

                        <!-- select tags -->
                        <v-card-text>
                            <v-row>
                                <v-col cols="12">
                                    <p
                                        class="
                                            mt-2
                                            mb-0
                                            caption
                                            font-weight-medium
                                        "
                                    >
                                        <v-icon class="mr-2"
                                            >turned_in_not</v-icon
                                        >Tag
                                    </p>
                                    <v-chip-group
                                        v-model="selectedTagId"
                                        active-class="primary dark"
                                        column
                                    >
                                        <v-chip
                                            v-for="item in plantTags"
                                            :key="item.node.tag.id"
                                            :value="item.node.tag.id"
                                            >{{ item.node.tag.tag }}</v-chip
                                        >
                                    </v-chip-group>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card-text>

                    <v-card-actions class="mr-5 pb-4">
                        <v-spacer></v-spacer>
                        <v-btn text @click="selectTagDialog = false"
                            >Cancel</v-btn
                        >
                        <v-btn
                            :disabled="!hasPermission('change_growthtask')"
                            outlined
                            rounded
                            color="primary"
                            :loading="isSaving"
                            @click="onSelectTagConfirmed"
                            >Save</v-btn
                        >
                    </v-card-actions>
                </v-card>
            </v-form>
        </v-dialog>

        <!-- Confirm phase dialog -->
        <ConfirmPhaseDialog
            :confirmPhaseDialogShow="confirmPhaseDialogShow"
            :growthTask="growthTask"
            :taskColor="taskColor"
            :currentPhase="currentPhase"
            :taskStatus="taskStatus"
            :nextPhase="nextPhase"
            :generateTaskPhaseTickLabels="generateTaskPhaseTickLabels"
            :isSaving="isSaving"
            :onConfirmGrowthTaskPhaseConfirmed="
                onConfirmGrowthTaskPhaseConfirmed
            "
            v-on:closeConfirmGrowthTaskPhaseWindow="
                closeConfirmGrowthTaskPhaseDialog
            "
        ></ConfirmPhaseDialog>

        <!-- QR code dialog -->
        <GrowthTaskQrCodeDialog
            :dialog.sync="qrCodeDialog"
            :task="task"
        ></GrowthTaskQrCodeDialog>

        <!-- Main -->
        <!-- task card -->
        <v-hover v-slot:default="{ hover }">
            <v-card
                class="my-3"
                min-height="100px"
                width="90%"
                tile
                outlined
                :elevation="hover ? 12 : 2"
                :class="{ 'on-hover': hover }"
            >
                <v-toolbar class="py-2" outlined flat>
                    <!-- Plant Name -->
                    <v-tooltip top>
                        <template v-slot:activator="{ on }">
                            <v-toolbar-title class="subtitle-1 pr-2" v-on="on">
                                {{ growthTask ? growthTask.plant.name : "" }}
                                <v-btn
                                    icon
                                    small
                                    color="grey"
                                    @click="
                                        goToPlantDetail(growthTask.plant.id)
                                    "
                                >
                                    <v-icon small>launch</v-icon>
                                </v-btn>
                                <div
                                    class="
                                        mt-n2
                                        grey--text
                                        text--darken-1
                                        caption
                                    "
                                    v-if="growthTask"
                                >
                                    {{
                                        (growthTask
                                            ? growthTask.growthStartDate
                                            : "1970-01-01")
                                            | moment("YYYY-[W]WW")
                                    }} / ID{{ growthTask ? growthTask.rawId : "" }}
                                </div>
                            </v-toolbar-title>
                        </template>
                        <span>{{
                            growthTask ? growthTask.plant.name : ""
                        }}</span>
                    </v-tooltip>

                    <!-- <v-tooltip top>
                        <template v-slot:activator="{ on }"> -->
                    <v-btn icon fab @click="qrCodeDialog = !qrCodeDialog">
                        <v-icon large>qr_code_2</v-icon>
                    </v-btn>
                    <!-- </template>
                        <span>QR Code</span>
                    </v-tooltip> -->

                    <v-spacer />

                    <!-- quantity -->
                    <v-icon :class="taskColor + `--text mr-1`">eco</v-icon>
                    <div :class="taskColor + `--text headline mr-2`">
                        {{ growthTask ? growthTask.totalInventory : "-" }}
                    </div>
                </v-toolbar>

                <!-- PotSize, Color, Environment, Tag -->
                <v-card-text>
                    <v-row class="px-2">
                        <v-chip class="mr-1 mb-1">
                            <v-avatar left>
                                <v-icon small>straighten</v-icon>
                            </v-avatar>
                            {{
                                growthTask && growthTask.potSize
                                    ? growthTask.potSize.potSizeCm
                                    : "-"
                            }}cm
                        </v-chip>
                        <v-chip class="mr-1 mb-1">
                            <v-avatar left>
                                <v-icon small>color_lens</v-icon>
                            </v-avatar>
                            {{
                                growthTask && growthTask.color
                                    ? growthTask.color.color
                                    : "-"
                            }}
                        </v-chip>
                        <v-chip class="mr-1 mb-1">
                            <v-avatar left>
                                <v-icon small>blur_on</v-icon>
                            </v-avatar>
                            {{
                                growthTask && growthTask.environment
                                    ? growthTask.environment.environment
                                    : "-"
                            }}
                        </v-chip>
                        <v-chip
                            class="mr-1 mb-1"
                            :outlined="growthTask.tag == null"
                            @click="selectTagDialog = true"
                        >
                            <v-avatar left>
                                <v-icon small>turned_in_not</v-icon>
                            </v-avatar>
                            {{
                                growthTask && growthTask.tag
                                    ? growthTask.tag.tag
                                    : "?"
                            }}
                        </v-chip>
                        <v-spacer />
                    </v-row>
                </v-card-text>

                <!-- Phase progress -->
                <v-card-text class="py-0">
                    <v-slider
                        :value="currentPhase.phaseNo - 1"
                        :tick-labels="
                            generateTaskPhaseTickLabels(
                                growthTask
                                    ? growthTask.growthTaskPhaseSet.edges
                                    : []
                            )
                        "
                        :color="taskColor"
                        track-color="grey lighten-3"
                        :max="growthTask ? growthTask.numPhases : 1"
                        :step="1"
                        ticks="always"
                        tick-size="4"
                        readonly
                    ></v-slider>
                </v-card-text>

                <!-- Space and Days -->
                <v-card-text class="py-0">
                    <v-row class="px-2 pt-3 align-center">
                        <v-icon>square_foot</v-icon>
                        <div class="subtitle">
                            {{ currentPhase.spaceM2 }} &#13217;
                        </div>

                        <v-spacer />

                        <!-- Closed -->
                        <v-tooltip top v-if="growthTask.totalInventory <= 0">
                            <template v-slot:activator="{ on }">
                                <v-chip v-on="on" color="grey" dark
                                    >Closed</v-chip
                                >
                            </template>
                            <span
                                >{{ currentPhase.startDate | moment("LL") }} ~
                                {{ currentPhase.endDate | moment("LL") }}</span
                            >
                        </v-tooltip>

                        <!-- Confirm phase button -->
                        <v-tooltip top v-else-if="isReadyForNextPhase">
                            <template v-slot:activator="{ on }">
                                <v-btn
                                    v-on="on"
                                    rounded
                                    small
                                    color="error"
                                    dark
                                    @click="confirmPhaseDialogShow = true"
                                >
                                    <v-icon small class="mr-2">warning</v-icon>
                                    {{
                                        nextPhase.isSalesPhase
                                            ? "Start Sale"
                                            : "Start P" + nextPhase.phaseNo
                                    }}
                                </v-btn>
                            </template>
                            <span
                                >{{ nextPhase.startDate | moment("LL") }} ~
                                {{ nextPhase.endDate | moment("LL") }}</span
                            >
                        </v-tooltip>

                        <!-- Remaining days -->
                        <v-tooltip top v-else>
                            <template v-slot:activator="{ on }">
                                <v-chip
                                    v-on="on"
                                    :color="taskColor + ` darken-1`"
                                    outlined
                                    class="px-1"
                                    style="min-width: 80px"
                                >
                                    <v-chip
                                        small
                                        style="min-width: 24px"
                                        :class="
                                            taskColor +
                                                ` darken-2 white--text px-1 mr-1 ml-0 justify-center`
                                        "
                                        >{{ phaseRemainingDays }}</v-chip
                                    >days
                                </v-chip>
                            </template>
                            <span
                                v-if="
                                    currentPhase.startDate &&
                                        currentPhase.endDate
                                "
                                >{{ currentPhase.startDate | moment("LL") }} ~
                                {{ currentPhase.endDate | moment("LL") }}</span
                            >
                        </v-tooltip>
                    </v-row>
                </v-card-text>

                <!-- Note -->
                <v-card-text v-if="growthTask && growthTask.note" class="pb-0">
                    <v-row class="px-0 mx-0">
                        {{
                            growthTask && growthTask.note
                                ? growthTask.note
                                : "-"
                        }}
                    </v-row>
                </v-card-text>

                <!-- action buttons -->
                <v-card-actions class="grey lighten-4 mt-4">
                    <!-- <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn icon small v-on="on">
                                <v-icon>visibility</v-icon>
                            </v-btn>
                        </template>
                        <span>Detail</span>
                    </v-tooltip>-->
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                icon
                                small
                                fab
                                @click="openUpdateTaskPlanDialog"
                                v-on="on"
                            >
                                <v-icon>event</v-icon>
                            </v-btn>
                        </template>
                        <span>Plan</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                icon
                                small
                                fab
                                @click="openSplitDialog"
                                :disabled="
                                    (growthTask
                                        ? growthTask.totalInventory
                                        : 0) <= 1
                                "
                                v-on="on"
                            >
                                <v-icon>call_split</v-icon>
                            </v-btn>
                        </template>
                        <span>Split</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                icon
                                small
                                fab
                                v-on="on"
                                @click="openSupplierDialog"
                            >
                                <v-icon>assignment</v-icon>
                            </v-btn>
                        </template>
                        <span>Supplier & Note</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                icon
                                small
                                fab
                                @click="openLockDialog"
                                v-on="on"
                            >
                                <v-icon
                                    :color="
                                        growthTask && growthTask.lockedDate
                                            ? `error`
                                            : ``
                                    "
                                    >lock</v-icon
                                >
                            </v-btn>
                        </template>
                        <span>{{
                            growthTask && growthTask.lockedDate
                                ? `Unlock`
                                : `Lock`
                        }}</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                icon
                                small
                                fab
                                @click="openChangeInventoryDialog"
                                v-on="on"
                            >
                                <v-icon>filter_1</v-icon>
                            </v-btn>
                        </template>
                        <span>Inventory</span>
                    </v-tooltip>
                    <v-spacer />
                    <v-tooltip v-if="hasPermission('delete_growthtask')" bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                icon
                                small
                                fab
                                @click="deleteTaskDialog = true"
                                v-on="on"
                            >
                                <v-icon>delete</v-icon>
                            </v-btn>
                        </template>
                        <span>Delete</span>
                    </v-tooltip>
                </v-card-actions>
            </v-card>
        </v-hover>
    </v-row>
</template>

<script>
import gql from "graphql-tag";
import helper from "@/utils/helper.js";
import DeleteDialog from "@/components/base/DeleteDialog.vue";
import InventoryChangeReasonTagEditDialog from "@/components/growth/InventoryChangeReasonTagEditDialog.vue";
import moment from "moment";
import GrowthTaskQrCodeDialog from "@/components/growth/GrowthTaskQrCodeDialog.vue";
//import GrowthTaskQrCode from "@/components/growth/GrowthTaskQrCode.vue";
import Lock from "@/components/growth/Lock.vue";
import UpdateTask from "@/components/growth/UpdateTask.vue";
import UpdateSupplierDialog from "@/components/growth/UpdateSupplierDialog.vue";
import SplitGrowthTaskDialog from "@/components/growth/SplitGrowthTaskDialog.vue";
import QRCodeSplitedTasks from "@/components/growth/QRCodeSplitedTasks.vue";
//import PlantTagDialog from "@/components/growth/PlantTagDialog.vue"
import GrowthTaskInventoryUpdateDialog from "@/components/growth/GrowthTaskInventoryUpdateDialog.vue";
import ConfirmPhaseDialog from "@/components/growth/ConfirmPhaseDialog.vue";

export default {
    name: "growth-task-card",
    props: ["task"],
    components: {
        DeleteDialog,
        InventoryChangeReasonTagEditDialog,
        GrowthTaskQrCodeDialog,
        //GrowthTaskQrCode ,
        Lock,
        UpdateTask,
        UpdateSupplierDialog,
        SplitGrowthTaskDialog,
        QRCodeSplitedTasks,
        //PlantTagDialog,
        GrowthTaskInventoryUpdateDialog,
        ConfirmPhaseDialog
    },

    apollo: {
        greenhouses: {
            query: gql`
                query greenhouses {
                    greenhouses {
                        edges {
                            node {
                                id
                                name
                                greenhouseDepartmentSet(
                                    isPlaceholder: false
                                    orderBy: "name"
                                ) {
                                    edges {
                                        node {
                                            id
                                            name
                                            greenhouseDepartmentSectionSet(
                                                isPlaceholder: false
                                                orderBy: "name"
                                            ) {
                                                edges {
                                                    node {
                                                        id
                                                        name
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            variables() {},
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => data.greenhouses,
            skip() {
                return !this.updateTaskPlanDialog;
            },
            pollInterval: 60000 // ms
        },

        suppliers: {
            query: gql`
                query suppliers {
                    suppliers(orderBy: "name") {
                        edges {
                            node {
                                id
                                name
                            }
                        }
                    }
                }
            `,
            variables() {},
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => data.suppliers,
            skip() {
                return !this.supplierDialog;
            },
            pollInterval: 60000 // ms
        },

        customers: {
            query: gql`
                query customers {
                    customers(orderBy: "name") {
                        edges {
                            node {
                                id
                                name
                            }
                        }
                    }
                }
            `,
            variables() {},
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => data.customers,
            skip() {
                return !this.lockDialog;
            },
            pollInterval: 60000 // ms
        },

        tenantInventoryChangeReasonTags: {
            query: gql`
                query tenantInventoryChangeReasonTags {
                    tenantInventoryChangeReasonTags(orderBy: "tag") {
                        edges {
                            node {
                                id
                                changeReason
                                tag
                            }
                        }
                    }
                }
            `,
            variables() {},
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => data.tenantInventoryChangeReasonTags,
            skip() {
                return !this.splitDialog && !this.changeInventoryDialog;
            }
        }
    },

    data: function() {
        return {
            isSaving: false,
            // isLoadingGreenhouseDepartmentSections: false,

            deleteTaskDialog: false,
            lockDialog: false,
            updateTaskPlanDialog: false,
            supplierDialog: false,
            splitDialog: false,
            splitedTasksDialog: false,
            selectTagDialog: false,
            confirmPhaseDialogShow: false,
            changeInventoryDialog: false,
            inventoryChangeReasonTagEditDialog: false,
            qrCodeDialog: false,

            // growthTask: this.task,
            selectedSupplierId: this.task.supplier
                ? this.task.supplier.id
                : null,
            selectedTagId: this.task.tag ? this.task.tag.id : null,

            greenhouses: {},
            suppliers: {},
            customers: {},

            lockingTaskData: {
                isEditing: true,
                isLocked: false,
                lockedDate: null,
                lockedForCustomerNodeId: null,
                lockedComment: null
            },
            lockingTaskDefault: {
                isEditing: true,
                isLocked: false,
                lockedDate: null,
                lockedForCustomerNodeId: null,
                lockedComment: null
            },
            splitingTaskData: {
                inventoryChangeReasonTag_Id: null,
                splitQuantity: null
            },
            splittedGrowthTask: {
                currentGrowthTask: null,
                newGrowthTask: null
            },
            growthTaskPhases: JSON.parse(
                JSON.stringify(this.task.growthTaskPhaseSet.edges)
            ),
            editingChangeReasonType: null,
            changeInventoryData: {
                toggleChangeInventoryButtonGroup: 1,
                inventoryChangeReasonTag_Id: null,
                changeQuantity: null,
                comment: null
            },
            rules: {
                required: v => !helper.isEmpty(v) || "This field is required",
                listRequired: v =>
                    (v || "").length >= 1 || `This field is required`,
                minLength: len => v =>
                    (v || "").length >= len || `Require at least ${len}`,
                maxLength: len => v => (v || "").length <= len || "Too long",
                maxNumber: num => v => (v || 1) <= num || "Out of range",
                startDateRule: index => v =>
                    ((index === 0 ||
                        moment(v).isAfter(
                            this.growthTaskPhases[index - 1].node.startDate
                        )) &&
                        (index === this.growthTaskPhases.length - 1 ||
                            moment(v).isBefore(
                                this.growthTaskPhases[index + 1].node.startDate
                            ))) ||
                    "Invliade start date"
            }
        };
    },
    computed: {
        growthTask() {
            return this.task;
        },
        today() {
            var _today = new Date();
            _today.setHours(0, 0, 0, 0); // remove time

            return _today;
        },
        todayStr() {
            var _now = new Date();

            return moment(_now).format("YYYY-MM-DD");
        },

        growthTaskFullUrl() {
            return this.baseUrl + "/#/growthTask/" + this.task.id;
        },
        baseUrl() {
            return location.origin;
        },

        greenhouseDepartmentSections() {
            var _sections = [];

            if (this.greenhouses.edges) {
                for (let greenhouse of this.greenhouses.edges) {
                    if (greenhouse.node.greenhouseDepartmentSet) {
                        for (let department of greenhouse.node
                            .greenhouseDepartmentSet.edges) {
                            if (
                                department.node.greenhouseDepartmentSectionSet
                            ) {
                                for (let section of department.node
                                    .greenhouseDepartmentSectionSet.edges) {
                                    _sections.push({
                                        id: section.node.id,
                                        name: section.node.name,
                                        fullName:
                                            greenhouse.node.name +
                                            "-" +
                                            department.node.name +
                                            "-" +
                                            section.node.name
                                    });
                                }
                            }
                        }
                    }
                }
            }

            return _sections;
        },
        plantTags() {
            var _plantTags = this.growthTask.plant.plantTagSet.edges;

            //sort
            _plantTags.sort((a, b) => {
                return a.node.tag.orderId == b.node.tag.orderId
                    ? 0
                    : a.node.tag.orderId > b.node.tag.orderId
                    ? 1
                    : -1;
            });

            return _plantTags;
        },
        currentPhase() {
            const _phases = this.growthTaskPhases.filter(
                item => item.node.isCurrentPhase
            );

            return _phases.length > 0
                ? _phases[0].node
                : {
                      phaseNo: 1,
                      greenhouseDepartmentSection: {},
                      startDate: new Date(),
                      endDate: new Date(),
                      confirmedDate: null
                  };
        },
        taskStatus() {
            if (!this.currentPhase) {
                return;
            }

            // planned
            if (
                (!this.currentPhase.isSalesPhase &&
                    this.currentPhase.phaseNo === 1 &&
                    this.currentPhase.confirmedDate === null) ||
                this.growthTask.growthStartDate > this.todayStr
            ) {
                return "PLANNED";
            }

            // sales
            if (this.currentPhase.isSalesPhase) {
                return "SALES";
            }

            // growing
            return "GROWING";
        },
        taskColor() {
            if (this.growthTask.totalInventory <= 0) {
                return "grey";
            }

            if (this.taskStatus === "PLANNED") {
                return "blue";
            }

            if (this.taskStatus === "GROWING") {
                return "success";
            }

            return "orange";
        },
        phaseRemainingWeeks() {
            if (this.taskStatus === "PLANNED") {
                return this.calculateWeeksBetweenDays(
                    this.currentPhase.startDate,
                    this.today
                );
            }

            return this.calculateWeeksBetweenDays(
                moment(this.currentPhase.endDate).add(1, "days"),
                this.today
            );
        },
        phaseRemainingDays() {
            if (this.taskStatus === "PLANNED") {
                return this.calculateDaysBetweenDays(
                    this.currentPhase.startDate,
                    this.today
                );
            }

            return this.calculateDaysBetweenDays(
                moment(this.currentPhase.endDate).add(1, "days"),
                this.today
            );
        },
        isReadyForNextPhase() {
            if (this.taskStatus === "SALES") {
                return false;
            }

            return this.phaseRemainingDays <= 0;
        },
        nextPhase() {
            if (this.taskStatus === "PLANNED" || this.taskStatus === "SALES") {
                return this.currentPhase;
            }

            const _nextPhases = this.growthTask.growthTaskPhaseSet.edges.filter(
                item => item.node.phaseNo == this.currentPhase.phaseNo + 1
            );

            return _nextPhases.length > 0
                ? _nextPhases[0].node
                : { greenhouseDepartmentSection: {} };
        },
        splitReasons() {
            return this.tenantInventoryChangeReasonTags
                ? this.tenantInventoryChangeReasonTags.edges.filter(item => {
                      return item.node.changeReason === "SPLIT";
                  })
                : [];
        },
        manualReasons() {
            return this.tenantInventoryChangeReasonTags
                ? this.tenantInventoryChangeReasonTags.edges.filter(item => {
                      return item.node.changeReason === "MANUAL";
                  })
                : [];
        }
    },
    created() {
        this.hasPermission = helper.hasPermission;
    },
    methods: {
        generateTaskPhaseTickLabels(phaseNodes) {
            var tickLabels = [];

            var i;
            for (i = 0; i < phaseNodes.length; i++) {
                tickLabels.push(
                    phaseNodes[i].node.isSalesPhase ? "Sales" : "P" + (i + 1)
                );
            }

            return tickLabels;
        },
        setNewSupplierId(newID) {
            this.selectedSupplierId = newID;
        },
        setNewTagId(newID) {
            this.selectedTagId = newID;
        },
        calculateSpaceM2(numPlantsPerM2, quantity) {
            return numPlantsPerM2 > 0
                ? Math.ceil(quantity / numPlantsPerM2)
                : 0;
        },

        calculateWeeksBetweenDays(date1, date2) {
            const startOfWeek1 = moment(date1).startOf("isoweek");
            const startOfWeek2 = moment(date2).startOf("isoweek");

            return startOfWeek1.diff(startOfWeek2, "week");
            // return moment(day1).diff(day2, "week");
        },

        calculateDaysBetweenDays(day1, day2) {
            return moment(day1).diff(moment(day2), "day");
        },

        onPhaseStartDateChanged(phase) {
            phase.startDatePicker = false;
            // console.log("onPhaseStartDateChanged: ", phase);

            // if phase no is not 1, then update previous phase NumDays
            if (phase.phaseNo > 1) {
                let previousPhase = this.growthTaskPhases.find(item => {
                    return item.node.phaseNo == phase.phaseNo - 1;
                });

                previousPhase.node.numDays = this.calculateDaysBetweenDays(
                    phase.startDate,
                    previousPhase.node.startDate
                );
            }

            // update following startDate of following phases
            let followingPhases = this.growthTaskPhases.filter(item => {
                return item.node.phaseNo > phase.phaseNo;
            });

            followingPhases.forEach((item, index) => {
                let prevNode = null;
                if (index === 0) {
                    prevNode = phase;
                } else {
                    prevNode = followingPhases[index - 1].node;
                }
                item.node.startDate = moment(prevNode.startDate)
                    .add(prevNode.numDays, "days")
                    .format("YYYY-MM-DD");
            });
        },

        onPhaseNumDaysChanged(phase) {
            // console.log("onPhaseNumDaysChanged: ", phase);

            // update following startDate of following phases
            let followingPhases = this.growthTaskPhases.filter(item => {
                return item.node.phaseNo > phase.phaseNo;
            });

            followingPhases.forEach((item, index) => {
                let prevNode = null;
                if (index === 0) {
                    prevNode = phase;
                } else {
                    prevNode = followingPhases[index - 1].node;
                }
                item.node.startDate = moment(prevNode.startDate)
                    .add(prevNode.numDays, "days")
                    .format("YYYY-MM-DD");
            });
        },

        // move task
        openUpdateTaskPlanDialog() {
            this.updateTaskPlanDialog = true;
            this.$refs.UpdateTaskDialogRef.getGrowthTaskIdData(this.task.id);
        },

        addPhase() {
            // get sales phase
            let salesPhase = this.growthTaskPhases.find(item => {
                return item.node.isSalesPhase;
            });

            let days = 7;

            // add phase
            this.growthTaskPhases.push({
                node: {
                    phaseNo: this.growthTaskPhases.length,
                    startDate: salesPhase.node.startDate,
                    numDays: days,
                    isSalesPhase: false,
                    isCurrentPhase: salesPhase.node.isCurrentPhase,
                    greenhouseDepartmentSection: {}
                }
            });

            // update sales phase
            salesPhase.node.phaseNo = this.growthTaskPhases.length;
            salesPhase.node.isCurrentPhase = false;
            salesPhase.node.startDate = moment(salesPhase.node.startDate)
                .add(days, "days")
                .format("YYYY-MM-DD");

            // sort phases
            this.growthTaskPhases.sort((a, b) => {
                return a.node.phaseNo - b.node.phaseNo;
            });
        },

        deletePhase(phase) {
            // remove phase
            this.growthTaskPhases = this.growthTaskPhases.filter(
                item => item.node.phaseNo !== phase.phaseNo
            );

            // update following startDate of following phases
            let followingPhases = this.growthTaskPhases.filter(item => {
                return item.node.phaseNo > phase.phaseNo;
            });

            // update following phaseNo and start date
            followingPhases.forEach((item, index) => {
                if (index === 0) {
                    item.node.startDate = moment(phase.startDate).format(
                        "YYYY-MM-DD"
                    );
                    if (phase.isCurrentPhase) {
                        item.node.isCurrentPhase = true;
                    }
                } else {
                    let prevNode = followingPhases[index - 1].node;
                    item.node.startDate = moment(prevNode.startDate)
                        .add(prevNode.numDays, "days")
                        .format("YYYY-MM-DD");
                }
                item.node.phaseNo = item.node.phaseNo - 1;
            });
        },

        onUpdateTaskPlanConfirmed(UpdateTaskPlanValidation) {
            if (!UpdateTaskPlanValidation) {
                return;
            }

            this.isSaving = true;

            let _totalGrowthDays = 0;
            let _maxSalesDays = 0;

            // calculate enddate of each phase
            this.growthTaskPhases.forEach((item, index) => {
                // for growth phase, use next phase's startDate as current phase's endDate
                if (index < this.growthTaskPhases.length - 1) {
                    item.node.endDate = moment(
                        this.growthTaskPhases[index + 1].node.startDate
                    )
                        .add(-1, "days")
                        .format("YYYY-MM-DD");

                    // item.node.numDays =
                    //     this.calculateDaysBetweenDays(
                    //         item.node.endDate,
                    //         item.node.startDate
                    //     ) + 1;

                    // console.log(
                    //     item.node.phaseNo,
                    //     item.node.isSalesPhase,
                    //     item.node.startDate,
                    //     item.node.endDate,
                    //     item.node.numDays
                    // );
                }

                // for sales phase, calculate endDate with maxSalesDays
                if (item.node.isSalesPhase) {
                    item.node.endDate = moment(item.node.startDate)
                        .add(item.node.numDays, "days")
                        .format("YYYY-MM-DD");

                    // console.log(
                    //     item.node.phaseNo,
                    //     item.node.isSalesPhase,
                    //     item.node.startDate,
                    //     item.node.endDate,
                    //     item.node.numDays
                    // );
                }

                // calcualte space m2
                item.node.spaceM2 = this.calculateSpaceM2(
                    item.node.numPlantsPerM2,
                    this.growthTask.totalInventory
                );

                // count total growth days and sales days
                if (item.node.isSalesPhase) {
                    _maxSalesDays += item.node.numDays;
                } else {
                    _totalGrowthDays += item.node.numDays;
                }
            });

            let _phases = this.growthTaskPhases.map(item => {
                return {
                    isSalesPhase: item.node.isSalesPhase,
                    phaseNo: item.node.phaseNo,
                    // isCurrentPhase: item.node.isCurrentPhase,
                    isCurrentPhase:
                        (item.node.phaseNo == 1 &&
                            moment(item.node.endDate).isSameOrAfter(
                                this.today,
                                "day"
                            )) ||
                        (item.node.isSalesPhase &&
                            moment(item.node.startDate).isSameOrBefore(
                                this.today,
                                "day"
                            )) ||
                        (moment(item.node.startDate).isSameOrBefore(
                            this.today,
                            "day"
                        ) &&
                            moment(item.node.endDate).isSameOrAfter(
                                this.today,
                                "day"
                            )),
                    startDate: item.node.startDate,
                    endDate: item.node.endDate,
                    numDays: item.node.numDays,
                    numPlantsPerM2: item.node.numPlantsPerM2,
                    spaceM2: item.node.spaceM2,
                    greenhouseDepartmentSectionNodeId:
                        item.node.greenhouseDepartmentSection.id
                };
            });

            // check if enddate valid

            const payload = {
                id: this.growthTask.id,
                numPhases: this.growthTask.numPhases,
                totalGrowthDays: _totalGrowthDays,
                maxSalesDays: _maxSalesDays,
                growthStartDate: moment(_phases[0].startDate).format(
                    "YYYY-MM-DD"
                ),
                growthEndDate: moment(
                    _phases[_phases.length - 2].endDate
                ).format("YYYY-MM-DD"),
                salesStartDate: moment(
                    _phases[_phases.length - 1].startDate
                ).format("YYYY-MM-DD"),
                salesEndDate: moment(
                    _phases[_phases.length - 1].endDate
                ).format("YYYY-MM-DD"),
                phases: _phases
            };

            this.$store
                .dispatch("growthTasks/updateGrowthTaskPhases", payload)
                .then(response => {
                    this.$emit("growthTaskPhasesUpdated", response);
                    // this.growthTask.tag = response.tag;
                    // this.$emit("update:growthTask", response);

                    this.closeUpdateTaskPlanDialog();
                })
                .catch(error => {
                    this.error = error.graphQLErrors[0].message;
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },
        closeSelectTagTaskDialog() {
            this.selectTagDialog = false;
        },
        closeUpdateTaskPlanDialog() {
            this.updateTaskPlanDialog = false;
        },

        // select task plant tag
        onSelectTagConfirmed() {
            this.isSaving = true;

            const payload = {
                id: this.growthTask.id,
                tagNodeId: this.selectedTagId
            };

            this.$store
                .dispatch("growthTasks/updateGrowthTaskTag", payload)
                .then(response => {
                    this.$emit("growthTaskTagUpdated", response);
                    this.growthTask.tag = response.tag;
                    // this.$emit("update:growthTask", response);

                    this.closeSelectTagDialog();
                })
                .catch(error => {
                    this.error = error.graphQLErrors[0].message;
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },

        closeSelectTagDialog() {
            this.selectTagDialog = false;
        },

        // confirm phase
        onConfirmGrowthTaskPhaseConfirmed(newGrowthTaskPhaseNodeId) {
            this.isSaving = true;

            const payload = {
                newGrowthTaskPhaseNodeId: newGrowthTaskPhaseNodeId
            };

            this.$store
                .dispatch("growthTasks/confirmGrowthTaskPhase", payload)
                .then(response => {
                    this.$emit("growthTaskPhaseConfirmed", response);
                    this.growthTask.growthTaskPhaseSet =
                        response.growthTaskPhaseSet;
                    // this.$emit("update:growthTask", response);

                    this.closeConfirmGrowthTaskPhaseDialog();
                })
                .catch(error => {
                    this.error = error.graphQLErrors[0].message;
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },

        closeConfirmGrowthTaskPhaseDialog() {
            this.confirmPhaseDialogShow = false;
        },

        // locking
        openLockDialog() {
            this.lockingTaskData = JSON.parse(
                JSON.stringify(this.lockingTaskDefault)
            );

            this.lockingTaskData.isLocked = this.growthTask.lockedDate
                ? true
                : false;
            // this.lockingTaskData.isEditing = !this.lockingTaskData.isLocked;
            this.lockingTaskData.lockedDate = this.growthTask.lockedDate;
            this.lockingTaskData.lockedForCustomerNodeId = this.growthTask
                .lockedForCustomer
                ? this.growthTask.lockedForCustomer.id
                : null;
            this.lockingTaskData.lockedComment = this.growthTask.lockedComment;

            if (this.$refs.lockForm) {
                this.$refs.lockForm.resetValidation();
            }

            this.lockDialog = true;
        },

        onLockGrowthTaskConfirmed(formValidity) {
            if (!formValidity) {
                return;
            }

            this.isSaving = true;

            const payload = {
                id: this.growthTask.id,
                lockedComment: this.lockingTaskData.lockedComment,
                lockedForCustomerNodeId: this.lockingTaskData
                    .lockedForCustomerNodeId
            };

            this.$store
                .dispatch("growthTasks/lockGrowthTask", payload)
                .then(response => {
                    this.$emit("taskLocked", response);
                    this.growthTask.lockedDate = response.lockedDate;
                    this.growthTask.lockedComment = response.lockedComment;
                    this.growthTask.lockedForCustomer =
                        response.lockedForCustomer;
                    // this.$emit("update:growthTask", response);

                    this.closeLockDialog();
                })
                .catch(error => {
                    this.error = error.graphQLErrors[0].message;
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },

        onUnlockGrowthTaskConfirmed() {
            const payload = {
                id: this.growthTask.id
            };

            this.isSaving = true;

            this.$store
                .dispatch("growthTasks/unlockGrowthTask", payload)
                .then(response => {
                    this.$emit("taskUnlocked", response);
                    this.growthTask.lockedDate = response.lockedDate;
                    this.growthTask.lockedComment = response.lockedComment;
                    this.growthTask.lockedForCustomer =
                        response.lockedForCustomer;
                    // this.$emit("update:growthTask", response);

                    this.closeLockDialog();
                })
                .catch(error => {
                    this.error = error.graphQLErrors[0].message;
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },

        closeLockDialog() {
            this.lockDialog = false;
            this.lockingTaskData = JSON.parse(
                JSON.stringify(this.lockingTaskDefault)
            );
        },

        // spliting
        openSplitDialog() {
            this.splitingTaskData.inventoryChangeReasonTag_Id = null;
            this.splitingTaskData.splitQuantity = null;

            if (this.$refs.splitForm) {
                this.$refs.splitForm.resetValidation();
            }

            this.splitDialog = true;
        },

        onSplitGrowthTaskConfirmed(splitFormValidity) {
            if (!splitFormValidity) {
                return;
            }

            this.isSaving = true;

            const payload = {
                id: this.growthTask.id,
                inventoryChangeReasonTagId: this.splitingTaskData
                    .inventoryChangeReasonTag_Id,
                splitQuantity: this.splitingTaskData.splitQuantity
            };

            this.$store
                .dispatch("growthTasks/splitGrowthTask", payload)
                .then(response => {
                    this.splittedGrowthTask = response;
                    // this.$emit("taskSplited", response);
                    this.splitDialog = false;
                    this.splitedTasksDialog = true;

                    this.closeSplitDialog();
                })
                .catch(error => {
                    this.error = error.graphQLErrors[0].message;
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },

        closeSplitDialog() {
            this.splitDialog = false;
            this.splitingTaskData.splitQuantity = null;
        },

        closeSplittedTaskDialog() {
            this.splittedGrowthTask = {
                currentGrowthTask: null,
                newGrowthTask: null
            };
            this.splitedTasksDialog = false;
            this.$emit("taskSplited");
        },

        // supplier
        openSupplierDialog() {
            this.supplierDialog = true;
        },

        onUpdateSupplierConfirmed(formValidity) {
            if (!formValidity) {
                return;
            }

            this.isSaving = true;

            const payload = {
                id: this.growthTask.id,
                note: this.growthTask.note,
                supplierNodeId: this.selectedSupplierId,
                supplierOrderNo: this.growthTask.supplierOrderNo,
                unitPurchasePrice: this.growthTask.unitPurchasePrice
            };

            this.$store
                .dispatch("growthTasks/updateGrowthTaskSupplier", payload)
                .then(response => {
                    this.$emit("taskSupplierUpdated", response);

                    this.closeSupplierDialog();
                })
                .catch(error => {
                    this.error = error.graphQLErrors[0].message;
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },

        closeSupplierDialog() {
            this.supplierDialog = false;
        },

        // change inventory

        openChangeInventoryDialog() {
            this.changeInventoryData.toggleChangeInventoryButtonGroup = 1;
            this.changeInventoryData.changeQuantity = null;
            this.changeInventoryData.comment = null;

            if (this.$refs.changeInventoryForm) {
                this.$refs.changeInventoryForm.resetValidation();
            }

            this.changeInventoryDialog = true;
        },

        onChangeInventoryConfirmed(formInventoryValidity) {
            if (!formInventoryValidity) {
                return;
            }

            this.isSaving = true;

            const payload = {
                id: this.growthTask.id,
                change:
                    this.changeInventoryData
                        .toggleChangeInventoryButtonGroup === 0
                        ? this.changeInventoryData.changeQuantity
                        : -this.changeInventoryData.changeQuantity,
                inventoryChangeReasonTagId: this.changeInventoryData
                    .inventoryChangeReasonTag_Id,
                comment: this.changeInventoryData.comment
            };

            this.$store
                .dispatch("growthTasks/updateGrowthTaskInventory", payload)
                .then(response => {
                    this.$emit("growthTaskInventoryUpdated", response);

                    this.closeChangeInventoryDialog();
                })
                .catch(error => {
                    // this.error = error.graphQLErrors[0].message;
                    this.error = error.graphQLErrors;
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },

        closeChangeInventoryDialog() {
            this.changeInventoryDialog = false;
            this.changeInventoryData.toggleChangeInventoryButtonGroup = 1;
            this.changeInventoryData.changeQuantity = null;
            this.changeInventoryData.comment = null;
        },

        // delete
        deleteTaskConfirmed() {
            if (this.growthTask) {
                const payload = {
                    id: this.growthTask.id
                };
                this.$store
                    .dispatch("growthTasks/deleteGrowthTask", payload)
                    .then(response => {
                        // close delete dialog
                        this.deleteTaskDialog = false;

                        this.$emit("taskDeleted", response);

                        // show snackbar
                        const payload = {
                            color: "success",
                            message: `The task is deleted!`
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);
                    })
                    .catch(error => {
                        this.error = error.graphQLErrors[0].message;
                    });
            }
        },

        goToPlantDetail(plantNodeId) {
            // this.$router.push({
            //     name: "plant-detail",
            //     params: { id: plant.id }
            // });

            let routeData = this.$router.resolve({
                name: "plant-detail",
                params: { id: plantNodeId }
            });
            window.open(routeData.href, "_blank");
        },

        addInventorySplitReasonTag() {
            this.editingChangeReasonType = "SPLIT";
            this.inventoryChangeReasonTagEditDialog = true;
        },

        addInventoryManualReasonTag() {
            this.editingChangeReasonType = "MANUAL";
            this.inventoryChangeReasonTagEditDialog = true;
        },

        onInventoryChangeReasonTagAdded() {
            this.editingChangeReasonType = null;
            this.$apollo.queries.tenantInventoryChangeReasonTags.refresh();
        }
    }
};
</script>